import * as React from 'react';
import { Alert, Box, Button, ButtonGroup, CircularProgress, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { Settings } from '../../controllers';
import { TokenValidator } from '../../utils';
import { createPtsOffsetView } from './ptsOffsetRender';

// Preview of the PTS offset on a sample Kline part (anonymous, served by the backend with a valid token).
// It follows the X/Y typed in the form before saving; it never changes any file.
const MAGNIFY = 5;

export default function PtsOffsetViewer({ offsetX, offsetY, offsetZ, pending }) {
    const canvasRef = React.useRef(null);
    const viewRef = React.useRef(null);
    const [status, setStatus] = React.useState('loading');
    const [message, setMessage] = React.useState('');
    const [magnify, setMagnify] = React.useState(false);
    const [view, setView] = React.useState('top');

    React.useEffect(() => {
        const created = createPtsOffsetView(canvasRef.current);
        viewRef.current = created;
        let alive = true;
        (async () => {
            const response = await Settings.GetPtsOffsetSample(await TokenValidator.GetInternalUser());
            if (!alive) return;
            const sample = response && response.success ? response.result : null;
            if (!sample || !sample.mesh || !Array.isArray(sample.pts)) {
                setStatus('error');
                setMessage(response && response.message ? response.message : 'Unable to load the sample part');
                return;
            }
            created.setSample(sample);
            setStatus('ready');
        })();
        return () => {
            alive = false;
            created.destroy();
            viewRef.current = null;
        };
    }, []);

    const factor = magnify ? MAGNIFY : 1;
    React.useEffect(() => {
        if (viewRef.current) viewRef.current.setOffset(Number(offsetX), Number(offsetY), Number(offsetZ), factor);
    }, [offsetX, offsetY, offsetZ, factor]);

    const choose = name => {
        setView(name);
        if (viewRef.current) viewRef.current.setView(name);
    };
    // same text as the PTS Offset screen: the decimals the value uses, at least one
    const fixed = value => Number(value || 0).toFixed(3).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '.0');
    const signed = value => `${Number(value) > 0 ? '+' : ''}${fixed(value)}`;
    const moved = Number(offsetX) !== 0 || Number(offsetY) !== 0 || Number(offsetZ || 0) !== 0;
    const onlyZHidden = view === 'top' && Number(offsetZ || 0) !== 0;

    return <Box>
        <Stack direction='row' gap={1} alignItems='center' flexWrap='wrap' sx={{mb: 1}}>
            <ButtonGroup size='small' disabled={status !== 'ready'}>
                {[['top', 'Top (X-Y)'], ['front', 'Side (X-Z)'], ['3d', '3D']].map(([name, label]) =>
                    <Button key={name} variant={view === name ? 'contained' : 'outlined'} onClick={() => choose(name)}>{label}</Button>)}
            </ButtonGroup>
            <Button size='small' startIcon={<ZoomInIcon />} disabled={status !== 'ready' || !moved}
                onClick={() => viewRef.current && viewRef.current.focusShift(8)}>Zoom to shift</Button>
            <FormControlLabel sx={{ml: 1}} control={<Switch size='small' checked={magnify} onChange={e => setMagnify(e.target.checked)} />}
                label={<Typography variant='body2'>Exaggerate ×{MAGNIFY} (display only)</Typography>} />
        </Stack>
        <Box sx={{position: 'relative', height: 420, border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden'}}>
            <canvas ref={canvasRef} style={{width: '100%', height: '100%', display: 'block', touchAction: 'none', cursor: 'grab'}} />
            {status === 'loading' && <Box sx={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                <CircularProgress size={28} />
            </Box>}
        </Box>
        {status === 'error' && <Alert severity='warning' sx={{mt: 1}}>{message}</Alert>}
        {status === 'ready' && onlyZHidden && <Typography variant='body2' sx={{mt: 1, color: '#1e88e5'}}>
            Z moves the line up or down: it cannot be seen from the top. Use Side (X-Z) or 3D.
        </Typography>}
        <Stack direction='row' gap={3} flexWrap='wrap' sx={{mt: 1}}>
            <Typography variant='body2' sx={{color: '#546e7a'}}>- - - original trim line (.pts)</Typography>
            <Typography variant='body2' sx={{color: '#d32f2f'}}>
                ——— {pending ? 'preview, not saved' : 'sent to Digital Dental'}: X {signed(offsetX)} mm, Y {signed(offsetY)} mm, Z {signed(offsetZ)} mm{magnify ? ` (drawn ×${MAGNIFY})` : ''}
            </Typography>
        </Stack>
        <Typography variant='caption' sx={{color: 'gray', display: 'block', mt: 0.5}}>
            Sample Kline part, preview only. Drag to rotate, Shift+drag or right-drag to pan, wheel to zoom. Axes are the ones of the .pts file.
        </Typography>
    </Box>;
}
